import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  TextField,
  Typography
} from "@material-ui/core";
import { Search as SearchIcon, Send as SendIcon, Trash2 as TrashIcon, Star as StarIcon } from "lucide-react";

import { i18n } from "../../translate/i18n";
import useFavoriteMessages from "../../hooks/useFavoriteMessages";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  listRoot: {
    padding: 0,
    maxHeight: 360,
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  listItem: {
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    alignItems: "flex-start"
  },
  listItemText: {
    marginRight: theme.spacing(2)
  },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  labelIcon: {
    color: theme.palette.warning.main
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  loadMoreWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(1)
  }
}));

const FavoriteMessageModal = ({ open, onClose, onSend }) => {
  const classes = useStyles();
  const { list, remove } = useFavoriteMessages();
  const [favorites, setFavorites] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [deletingFavorite, setDeletingFavorite] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const resetPagination = useCallback(() => {
    setFavorites([]);
    setPageNumber(1);
    setHasMore(false);
  }, []);

  useEffect(() => {
    if (open) {
      resetPagination();
    }
  }, [open, resetPagination]);

  const fetchFavorites = useCallback(async () => {
    if (!open) return;

    setLoading(true);
    try {
      const { records, hasMore: nextHasMore } = await list({
        pageNumber,
        searchParam
      });

      setFavorites((prev) => {
        if (pageNumber === 1) return records;
        const next = [...prev];
        records.forEach((record) => {
          if (!next.find((item) => item.id === record.id)) {
            next.push(record);
          }
        });
        return next;
      });
      setHasMore(Boolean(nextHasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [list, open, pageNumber, searchParam]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleSearchChange = (event) => {
    setSearchParam(event.target.value);
    setPageNumber(1);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPageNumber((prev) => prev + 1);
    }
  };

  const handleSend = (favorite) => {
    onSend(favorite);
  };

  const handleConfirmRemove = async () => {
    if (!deletingFavorite) return;
    try {
      await remove(deletingFavorite.id);
      setFavorites((prev) => prev.filter((item) => item.id !== deletingFavorite.id));
      setDeletingFavorite(null);
      setConfirmOpen(false);
    } catch (err) {
      toastError(err);
    }
  };

  const buildPreview = useCallback((favorite) => {
    const message = favorite?.message;
    if (!message) return i18n.t("favoriteMessages.previewUnavailable");

    if (message.mediaUrl) {
      if (message.body) return message.body;
      return i18n.t("favoriteMessages.mediaOnly");
    }

    return message.body || i18n.t("favoriteMessages.noText");
  }, []);

  const emptyState = useMemo(() => {
    if (loading) {
      return (
        <div className={classes.emptyState}>
          <CircularProgress size={20} />
        </div>
      );
    }

    if (!favorites.length) {
      return (
        <div className={classes.emptyState}>
          {i18n.t("favoriteMessages.empty")}
        </div>
      );
    }

    return null;
  }, [classes.emptyState, favorites.length, loading]);

  return (
    <>
      <ConfirmationModal
        title={i18n.t("favoriteMessages.confirmation.title")}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleConfirmRemove}
      >
        {i18n.t("favoriteMessages.confirmation.message")}
      </ConfirmationModal>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{i18n.t("favoriteMessages.title")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            value={searchParam}
            onChange={handleSearchChange}
            placeholder={i18n.t("favoriteMessages.searchPlaceholder")}
            margin="dense"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              )
            }}
          />
          {emptyState}
          {favorites.length > 0 && (
            <List className={classes.listRoot}>
              {favorites.map((favorite) => (
                <ListItem
                  key={favorite.id}
                  className={classes.listItem}
                  button
                  onClick={() => handleSend(favorite)}
                >
                  <ListItemText
                    className={classes.listItemText}
                    primary={
                      <div className={classes.labelRow}>
                        <StarIcon size={14} className={classes.labelIcon} />
                        <Typography variant="subtitle2">{favorite.label}</Typography>
                      </div>
                    }
                    secondary={buildPreview(favorite)}
                  />
                  <div className={classes.actionsRow}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setDeletingFavorite(favorite);
                        setConfirmOpen(true);
                      }}
                      aria-label={i18n.t("favoriteMessages.remove")}
                    >
                      <TrashIcon size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSend(favorite);
                      }}
                      aria-label={i18n.t("favoriteMessages.send")}
                    >
                      <SendIcon size={16} />
                    </IconButton>
                  </div>
                </ListItem>
              ))}
            </List>
          )}
          {hasMore && (
            <div className={classes.loadMoreWrapper}>
              <Button
                onClick={handleLoadMore}
                disabled={loading}
                size="small"
                color="primary"
              >
                {i18n.t("favoriteMessages.loadMore")}
              </Button>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            {i18n.t("favoriteMessages.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

FavoriteMessageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired
};

export default FavoriteMessageModal;
